import { create } from "zustand"

/**
 * Keyboard Zustand store — tracks native keyboard state.
 *
 * Ported from v1's keyboard handling in useCapacitorLifecycle.
 * Stores the keyboard height and visibility for layout adjustments
 * on Capacitor native platforms (iOS / Android).
 *
 * `resizeMode` tracks which Capacitor KeyboardResize mode is active:
 *   - "native" (default): WKWebView resizes when keyboard opens.
 *     `container.clientHeight` already accounts for the keyboard.
 *   - "none": WKWebView stays full-size, keyboard overlays from the bottom.
 *     Scroll calculations must subtract `keyboard.height` from visible area.
 */

interface KeyboardInfo {
  height: number
  isOpen: boolean
}

interface KeyboardState {
  keyboard: KeyboardInfo | null
  /** Current Capacitor KeyboardResize mode. Default: "native". */
  resizeMode: "native" | "none"

  // ── Actions ──
  setKeyboard: (info: KeyboardInfo) => void
  clearKeyboard: () => void
  setResizeMode: (mode: "native" | "none") => void
}

export const useKeyboardStore = create<KeyboardState>((set) => ({
  keyboard: null,
  resizeMode: "native",

  setKeyboard: (info) => set({ keyboard: info }),
  clearKeyboard: () => set({ keyboard: null }),
  setResizeMode: (mode) => set({ resizeMode: mode }),
}))

/**
 * Returns the number of pixels at the bottom of the viewport that are
 * obscured by the keyboard overlay. Returns 0 when the keyboard isn't
 * open or when the resize mode is "native" (WebView already resized).
 */
export function getKeyboardOverlayHeight(): number {
  const { keyboard, resizeMode } = useKeyboardStore.getState()
  if (!keyboard?.isOpen) return 0
  if (resizeMode === "native") return 0
  return keyboard.height
}
